import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { OrderItem } from '@cat-ecom/common/utils/productFormUtils';
import {
  getSessionStorage,
  removeSessionStorage,
  setSessionStorage
} from '@cat-ecom/common/utils/storageUtils';
import { AlternativeSet } from 'components/alternative-parts-drawer/constants';
import {
  AVAILABILITY,
  VIEWS,
  DEFAULT_CART_SETTINGS,
  DEFAULT_CART_SETTINGS_DEALER_AND_CSP
} from 'components/cart-settings-drawer/constants';
import {
  HEADLESS_PICKUP_AND_DELIVERY_PAGE,
  LEGACY_PICKING_UP_AND_DELIVERY_ROUTE,
  LEGACY_SUMMARY_AND_PAYMENT_ROUTE
} from 'constants/links';
import { getPartIndex } from 'services/cart/utils';
import {
  CART_FLOW,
  CART_IMPORT_ERROR,
  CART_IMPORT_FLOW
} from 'services/dealer-locator/constants';
import { UserCustomerTypes } from 'services/user/types';
import { PriceAvailabilityLegacyItem } from 'services/priceAvailabilityLegacy/types';
import { SavedList } from 'services/savedLists/types';
import {
  DisplayNotesSettings,
  Fulfillment
} from 'services/userPreferences/types';
import { deleteCookie, getCookie } from 'utils/cookieUtils';
import { getFormattedURL, getRelativeURLWithParams } from 'utils/url';
import { RP_COOKIE } from '../constants/common';
import { CartResponse, OrderItemsResponse } from 'services/cart/types';
import { OrderItemPart } from 'services/cart';
import { dealerCustomerInstructionAndOptions } from 'services/dealer-customer/types';
import { normalizeCommerceLink } from 'utils/links';

export const mapAlternativeData = (
  alternativeData: PriceAvailabilityLegacyItem[],
  fieldItems?: OrderItem[]
) =>
  alternativeData?.map((part, index) => {
    const referenceId = part?.referenceId;
    return {
      originalPart: {
        ...part,
        originalPart: part.partNumber,
        index: getPartIndex(alternativeData, part.partNumber, index),
        quantity: mapQuantityByReferenceId(
          referenceId,
          part?.quantity,
          fieldItems
        )
      },
      alternativeParts: [
        ...part.remanParts,
        ...part.classicParts,
        ...part.alternateParts
      ].map((altPart) => ({
        ...altPart,
        quantity: 0,
        originalSosCode: part.sosCode,
        originalPart: part.partNumber,
        index: getPartIndex(alternativeData, part.partNumber, index)
      })),
      hasError: false
    };
  }) as AlternativeSet[];

export const mapQuantityByReferenceId = (
  referenceId: string | undefined,
  fallbackQuantity: number,
  fieldItems: OrderItem[]
): number => {
  if (!referenceId || !fieldItems) return fallbackQuantity;

  const matchingItem = fieldItems?.find(
    (item) => item?.referenceId === referenceId
  );

  return Number(matchingItem?.quantity) || fallbackQuantity;
};

export const validateAlternativeSet = (set: AlternativeSet) =>
  [{ ...set.originalPart }, ...set.alternativeParts].some(
    (item) => Number(item?.quantity) > 0
  );

export const getValidAlternativeSet = (set: AlternativeSet) =>
  [{ ...set.originalPart }, ...set.alternativeParts].filter(
    (item) => Number(item?.quantity) > 0
  );

export const getSavedListsFlags = (items: SavedList[]) => {
  const { ownSavedLists, favoriteSavedLists } = items.reduce(
    (acc, item) => {
      if (item.isSelfList && item.orderItemsQuantity > 0) {
        acc.ownSavedLists.push(item);
      }
      if (item.isFavorite && item.orderItemsQuantity > 0) {
        acc.favoriteSavedLists.push(item);
      }
      return acc;
    },
    { ownSavedLists: [], favoriteSavedLists: [] }
  );
  const hasOwnSavedListsFlag = ownSavedLists.length > 0;
  const hasFavoriteSavedListsFlag = favoriteSavedLists.length > 0;
  return { hasOwnSavedListsFlag, hasFavoriteSavedListsFlag };
};

export function hasDuplicates(array: Array<unknown>) {
  return new Set(array).size !== array.length;
}

interface GetCheckoutUrlParams {
  skipPickupAndDeliveryPage: boolean;
  catalogId: string;
  langId: string;
  storeId: string;
  pudFeatureFlag?: boolean;
  displayPrefsWarning?: boolean;
  isComplianceCheckFailed?: boolean;
  isTermsAndConditionsCheckFailed?: boolean;
}

export const getCheckoutURL = ({
  skipPickupAndDeliveryPage,
  catalogId,
  langId,
  storeId,
  pudFeatureFlag = false,
  displayPrefsWarning = false,
  isComplianceCheckFailed = false,
  isTermsAndConditionsCheckFailed = false
}: GetCheckoutUrlParams) => {
  const complianceError = '_ERR_GTS_COMPLIANCE';
  const [, language, esite] = window.location.pathname.split('/');
  const headlessPudPage = `/${language}/${esite}/${HEADLESS_PICKUP_AND_DELIVERY_PAGE}`;
  // Redirect to the PUD page (if 'Skip PUD' is enabled)
  // 1. Then display a system feedback error when the user's preferred delivery method is missing from the dealer customer's available delivery methods
  // 2. Then display a compliance error when the compliance check fails
  // 3. Then display a terms and conditions error when the terms and conditions check fails
  const shouldSkipSummaryPage =
    skipPickupAndDeliveryPage &&
    !displayPrefsWarning &&
    !isComplianceCheckFailed &&
    !isTermsAndConditionsCheckFailed;
  const checkoutRoute = normalizeCommerceLink(
    shouldSkipSummaryPage
      ? LEGACY_SUMMARY_AND_PAYMENT_ROUTE
      : LEGACY_PICKING_UP_AND_DELIVERY_ROUTE
  );
  const params: Record<string, string | boolean> = {
    catalogId,
    langId,
    storeId
  };
  if (displayPrefsWarning) {
    params.displayPrefsWarning = true;
  }
  if (isComplianceCheckFailed) {
    params.error = complianceError;
  }
  if (isTermsAndConditionsCheckFailed) {
    params.displayTermsWarning = true;
  }
  const newURLwithParams = getRelativeURLWithParams(checkoutRoute, params);

  if (pudFeatureFlag && !skipPickupAndDeliveryPage) {
    return headlessPudPage;
  } else {
    return getFormattedURL(window.location.hostname, newURLwithParams);
  }
};

export const hasLineItemNotes = (displayNotes: DisplayNotesSettings) => {
  return displayNotes && Object.values(displayNotes).some(Boolean);
};

export const getSerialNumber = (
  items: OrderItem[],
  previousEquipment: string
) => {
  const filteredItems =
    items?.filter((item) => item?.serialNumber !== '') || [];
  const firstSerialNumber = filteredItems?.[0]?.serialNumber?.trim() || '';
  const hasSameEquipments =
    filteredItems.every(
      (item) => item?.serialNumber?.trim() === firstSerialNumber
    ) || false;
  return hasSameEquipments && !previousEquipment
    ? firstSerialNumber
    : previousEquipment;
};

export const removeCookie = (name: string) => {
  if (getCookie(name)) {
    document.cookie = `${name}=; max-age=0; path=/;`;
  }
};
export const getFilteredSchema = (schema, columnToHide) => {
  return schema.filter((item) => item.accessorKey !== columnToHide);
};

export const getCartSettingsDefaultValues = (shoppingCartPrefs) => ({
  ...shoppingCartPrefs,
  standardShowAvailability:
    shoppingCartPrefs?.pageView === VIEWS.STANDARD
      ? shoppingCartPrefs?.showAvailability
      : AVAILABILITY.BY_DATE,
  compactShowAvailability:
    shoppingCartPrefs?.pageView === VIEWS.COMPACT
      ? shoppingCartPrefs?.showAvailability
      : AVAILABILITY.BY_SOURCE
});

export const checkDuplicates = (part) => {
  if (isEmpty(part)) {
    return false;
  }
  const itemNumbers = part?.map((el) => el.itemNumber);
  return hasDuplicates(itemNumbers);
};

export const isCartFlowValid = (
  isEmptyCartDetails: boolean,
  isCartImportAllowedFlag: boolean
) => {
  const isEmptyCartImportFlow = getSessionStorage(CART_FLOW);
  const cartImportError = getSessionStorage(CART_IMPORT_ERROR);
  let isCartFlowValid = false;

  // check cartDetails is empty and user already pass for the cart import flow
  if (isEmptyCartDetails && isEmptyCartImportFlow) {
    deleteCookie(CART_IMPORT_FLOW);
  } else if (isEmptyCartDetails && !isEmptyCartImportFlow && !cartImportError) {
    // Cheking if we don't have anything on the cart and it is the first time the user is trying to do cart import
    setSessionStorage(CART_FLOW, 'true');
    deleteCookie(CART_IMPORT_FLOW);
    removeSessionStorage(CART_IMPORT_ERROR);
    isCartFlowValid = true;
  } else if (!isEmptyCartDetails && isCartImportAllowedFlag) {
    // Checking if the cart is not empty and we don't have any error/warning on the severity
    deleteCookie(CART_IMPORT_FLOW);
    removeSessionStorage(CART_IMPORT_ERROR);
    removeSessionStorage(CART_FLOW);
    isCartFlowValid = true;
  } else {
    deleteCookie(CART_IMPORT_FLOW);
  }
  return isCartFlowValid;
};

export const deleteRPCookie = () => {
  deleteCookie(RP_COOKIE);
};

export const getExactEquipmentMatch = (
  equipmentSelected: string,
  equipmentList: Equipment[]
) => {
  const equipment =
    equipmentSelected
      ?.toLowerCase()
      .replace(/[^a-z0-9]/gi, '')
      .trim() ?? '';

  return (equipmentList ?? []).filter(
    ({ model = '', serialNumber = '', assetName = '' }) =>
      equipment?.includes(model?.toLowerCase()) &&
      (!serialNumber || equipment?.includes(serialNumber?.toLowerCase())) &&
      (!assetName || equipment?.includes(assetName?.toLowerCase()))
  );
};

export const getPartialEquipmentMatches = (
  equipmentSelected: string,
  equipmentList: Equipment[]
) => {
  const equipment = (equipmentSelected ?? '')
    .replace(/[^\w\s-]/g, '')
    .toLowerCase()
    .split(/[\s-]+/);

  return (equipmentList ?? []).filter(
    ({ model = '', serialNumber = '', assetName = '' }) =>
      equipment?.every(
        (equip) =>
          model?.toLowerCase()?.includes(equip) ||
          serialNumber?.toLowerCase()?.includes(equip) ||
          assetName?.toLowerCase()?.includes(equip)
      )
  );
};

export const getPartName = (item: OrderItemsResponse, itemSummary: Product) => {
  const isCatPart = item?.orderItemLevelFlags?.isCatPart;
  const isDealerPart = itemSummary?.isDealerPart;
  const nameOrDescription = itemSummary?.name || item.description;
  return `${item.partNumber}: ${isCatPart && !isDealerPart ? nameOrDescription : item.description}`;
};

export const shouldShowLinkToPdp = (
  item: OrderItemsResponse,
  itemSummary: Product
) => {
  const isFluidPart = itemSummary?.fluidAttributes?.length > 0;
  const isSeoUrlAvailable = Boolean(itemSummary?.seoURL);
  const isCatPart = item?.orderItemLevelFlags?.isCatPart;
  const isDealerPart = itemSummary?.isDealerPart;
  return (isCatPart || isDealerPart) && (isSeoUrlAvailable || isFluidPart);
};

export const getUpdatedSsroBundles = (
  item: OrderItemPart,
  selectedBundles: number[]
) => {
  return (item?.ssroBundle ?? [])?.filter(
    (bundle) => !(selectedBundles ?? [])?.includes(bundle?.selfServiceOptionId)
  );
};

export const formatEquipmentDisplayText = (equipment: {
  model: string;
  serialNumber: string;
  assetName: string;
}) => {
  const { model = '', serialNumber = '', assetName = '' } = equipment;

  if (assetName && model && serialNumber) {
    return `${assetName} (${model} - ${serialNumber})`;
  }

  if (model && serialNumber) {
    return `${model} - ${serialNumber}`;
  }

  return model || '';
};

export const getDefaultCartSettings = ({
  isCspUser,
  customerType
}: {
  isCspUser: boolean;
  customerType: UserCustomerTypes;
}) => {
  const isDealerUser = UserCustomerTypes.Dealer === customerType;

  const defaultView =
    isDealerUser || isCspUser
      ? DEFAULT_CART_SETTINGS_DEALER_AND_CSP
      : DEFAULT_CART_SETTINGS;

  return defaultView;
};

export const shouldDisplayPrefsWarning = (
  selectedFulfillment: Fulfillment,
  fulfillmentShipViaOptions: dealerCustomerInstructionAndOptions[]
) => {
  const preferredLabel =
    selectedFulfillment?.delivery?.methodLabel?.trim() || '';
  if (isEmpty(preferredLabel)) return false;

  return !fulfillmentShipViaOptions?.some(
    ({ label }) => label?.trim() === preferredLabel
  );
};

export const getNonNullFulfillmentEntry = (obj: Fulfillment) => {
  return Object.fromEntries(
    Object.entries(obj)?.filter(([_, value]) => value !== null)
  );
};

export const getCartSubTotal = (
  latestCartDetails: CartResponse,
  subTotal: string,
  applyGst: boolean
) => {
  const latestFormattedTotals =
    latestCartDetails?.orderItemPriceAvailabilityTotals?.orderTotals?.formatted;
  const calculatedOrderSubtotal = applyGst
    ? latestFormattedTotals?.orderSubTotalWithGST
    : latestFormattedTotals?.orderSubTotal;
  return calculatedOrderSubtotal || subTotal;
};
